// "My Time" dashboard — WI-11822.
//
// Single-fetch dashboard backed by GET /time/my-dashboard:
//   - Greeting + week number + reporting-alert banner
//   - 4 KPI cards (today / week / month / billable share)
//   - Production rate chart (last 8 weeks, stacked daily bars by customer)
//   - Where time goes (donut + customer breakdown, last 30 days)
//   - Compliance heatmap (last 4 weeks, Mon–Fri)
//   - By activity (last 30 days, horizontal bars)
//   - Planned work (active phase_resources with budget vs. logged + status pill)
//   - Azure DevOps tasks (active items assigned to me)
//
// Colors map the design's hex palette to Forge OKLCH tokens. Customer colors
// are stable per customer-id via a small palette.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle, ArrowRight, Clock, Plus, ExternalLink, Eye, TrendingUp,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type {
  MyDashboardResponse, ProductionRateRow, PlannedWorkStatus, MyBudgetResponse,
} from '@/types/api'

// WI-11888 PR 3: minimal shape of the /me capability payload we read here.
interface MeFlags { canImpersonate: boolean }
interface ImpersonatableUser { id: number; displayName: string; email: string }

// ─── Customer palette — stable colour per customer id ────────────────────────

const CUSTOMER_PALETTE = [
  'oklch(0.55 0.18 250)',  // blue
  'oklch(0.55 0.18 295)',  // purple
  'oklch(0.62 0.16 50)',   // orange
  'oklch(0.55 0.16 140)',  // green
  'oklch(0.55 0.16 25)',   // red
  'oklch(0.50 0.04 250)',  // grey (fallback / "Internal")
]

function customerColor(id: number | null): string {
  if (id == null) return CUSTOMER_PALETTE[CUSTOMER_PALETTE.length - 1]
  // Deterministic hash so the same customer keeps the same colour across rerenders.
  return CUSTOMER_PALETTE[Math.abs(id) % (CUSTOMER_PALETTE.length - 1)]
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtH(n: number, digits = 1): string {
  if (n === 0) return '0h'
  return `${n.toFixed(digits).replace(/\.0$/, '')}h`
}

function fmtDateLong(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
}

function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

// ─── Page ────────────────────────────────────────────────────────────────────

// Default export: full-page version (used at /my-time, kept as a redirect
// target for old bookmarks).
export default function MyTimePage() {
  return (
    <div className="page">
      <MyTimeDashboardContent showHeader />
    </div>
  )
}

// Embeddable content — used inside TimePage as the default tab (WI-11829).
// `onLogTime` is invoked by the "Log time" CTA when embedded; the full-page
// version omits it and the CTA falls back to a /time link.
export function MyTimeDashboardContent({ showHeader = true, onLogTime }: {
  showHeader?: boolean
  onLogTime?:  () => void
}) {
  // WI-11888 PR 3: "viewing as" picker for Admin / Time Administrator / Manager.
  // When asUser is set the dashboard reflects the impersonated user; the
  // backend enforces the impersonation rules.
  const [asUserId, setAsUserId] = useState<number | null>(null)

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn:  () => api.get<MeFlags>('/me'),
  })
  const canImpersonate = me?.canImpersonate ?? false

  const { data: impUsers = [] } = useQuery({
    queryKey: ['time', 'impersonatable-users'],
    queryFn:  () => api.get<ImpersonatableUser[]>('/time/impersonatable-users'),
    enabled:  canImpersonate,
  })

  const dashUrl = asUserId
    ? `/time/my-dashboard?asUser=${asUserId}`
    : '/time/my-dashboard'

  const { data, isLoading, isError } = useQuery({
    queryKey: ['time', 'my-dashboard', asUserId],
    queryFn:  () => api.get<MyDashboardResponse>(dashUrl),
  })

  if (isLoading) return <p className="muted" style={{ paddingTop: 40, textAlign: 'center' }}>Loading…</p>
  if (isError || !data) return <p className="muted" style={{ paddingTop: 40, textAlign: 'center' }}>Failed to load dashboard.</p>

  return (
    <div className="mtd">
      {showHeader && (
        <div className="page-head" style={{ alignItems: 'flex-end' }}>
          <div>
            <div className="page-title">{greeting()}, {data.me.firstName}</div>
            <div className="page-subtitle">
              {fmtDateLong(data.today)} · Week {data.weekNumber}
            </div>
          </div>
          {onLogTime ? (
            <button className="btn btn-brand" onClick={onLogTime}>
              <Plus size={14} />
              Log time
            </button>
          ) : (
            <Link to="/time" className="btn btn-brand">
              <Plus size={14} />
              Log time
            </Link>
          )}
        </div>
      )}

      {canImpersonate && impUsers.length > 0 && (
        <div className="card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Eye size={14} style={{ color: 'var(--ns-brand-strong)' }} />
          <span className="muted" style={{ fontSize: 12.5 }}>Viewing as</span>
          <select
            className="input"
            style={{ height: 28, fontSize: 13, padding: '0 8px', minWidth: 220 }}
            value={asUserId ?? ''}
            onChange={e => setAsUserId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— Myself —</option>
            {impUsers.map(u => (
              <option key={u.id} value={u.id}>{u.displayName}</option>
            ))}
          </select>
          {asUserId !== null && (
            <span className="muted" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              Read-only view of this user's dashboard
            </span>
          )}
        </div>
      )}

      {/* ── Reporting alert ─────────────────────────────────────────────── */}
      {data.reportingAlert.missingDays.length > 0 && (
        <ReportingAlert missing={data.reportingAlert.missingDays} lastSubmission={data.reportingAlert.lastSubmission} />
      )}

      {/* ── KPI grid ────────────────────────────────────────────────────── */}
      <KpiGrid k={data.kpis} />

      {/* ── Budget tracker ──────────────────────────────────────────────── */}
      <BudgetCard />

      {/* ── Production rate + Where time goes ───────────────────────────── */}
      <div className="mtd-grid-2-1">
        <ProductionRateCard rows={data.productionRate} weekStart={data.weekStart} />
        <WhereTimeGoesCard total={data.whereTimeGoes.totalHours} rows={data.whereTimeGoes.rows} />
      </div>

      {/* ── Compliance heatmap + By activity ────────────────────────────── */}
      <div className="mtd-grid-2-1">
        <ComplianceCard days={data.compliance.days} weekStart={data.weekStart} />
        <ByActivityCard rows={data.byActivity} />
      </div>

      {/* ── Planned work ────────────────────────────────────────────────── */}
      <PlannedWorkCard rows={data.plannedWork} />

      {/* ── ADO ─────────────────────────────────────────────────────────── */}
      <AdoCard rows={data.adoTasks} />
    </div>
  )
}

// ── Reporting alert ──────────────────────────────────────────────────────────

function ReportingAlert({ missing, lastSubmission }: {
  missing: { date: string; hoursLogged: number; hoursMissing: number }[]
  lastSubmission: string | null
}) {
  const summary = missing.map(m => {
    const d = new Date(m.date + 'T00:00:00')
    const wd = d.toLocaleDateString('en-GB', { weekday: 'long' })
    const md = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    return m.hoursLogged === 0
      ? `${wd} ${md} (entire day)`
      : `${wd} ${md} (${m.hoursMissing.toFixed(1)}h missing)`
  }).join(' and ')

  return (
    <div className="mtd-alert">
      <div className="mtd-alert-icon"><AlertTriangle size={16} /></div>
      <div className="mtd-alert-body">
        <strong>You're behind on time reporting.</strong> {summary} need{missing.length === 1 ? 's' : ''} entries.
        {lastSubmission && (
          <> Your last entry was {new Date(lastSubmission).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}.</>
        )}
      </div>
      <Link to="/time" className="mtd-alert-cta">Catch up <ArrowRight size={12} /></Link>
    </div>
  )
}

// ── Revenue formatting helpers ────────────────────────────────────────────────

function fmtSEK(n: number): string {
  return Math.abs(n).toLocaleString('sv-SE', { maximumFractionDigits: 0 }) + ' kr'
}

function fmtSEKCompact(n: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} Mkr`
  if (abs >= 10_000)    return `${Math.round(n / 1_000)} tkr`
  if (abs >= 1_000)     return `${(n / 1_000).toFixed(1)} tkr`
  return `${Math.round(n)} kr`
}

// ── Budget tracker ────────────────────────────────────────────────────────────

function BudgetCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'my-budget'],
    queryFn:  () => api.get<MyBudgetResponse>('/finance/my-budget'),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading || !data || !data.configured || !data.months?.length) return null

  const today    = new Date()
  const curMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  const bm       = data.months.find(m => m.month === curMonth)
  const hasBudget = !!bm && bm.budgetAmount > 0

  const monthPct = hasBudget
    ? Math.min(100, (bm!.reportedRevenue / bm!.budgetAmount) * 100)
    : 0

  const daysPct = bm && bm.workingDays > 0
    ? (() => {
        let elapsed = 0
        const d = new Date(today.getFullYear(), today.getMonth(), 1)
        while (d <= today) {
          if (d.getDay() !== 0 && d.getDay() !== 6) elapsed++
          d.setDate(d.getDate() + 1)
        }
        return Math.min(100, (elapsed / bm.workingDays) * 100)
      })()
    : 0

  const variance = data.ytdVariance ?? 0
  const isAhead  = variance >= 0

  const versionLabel = data.versionName
    ? `${data.isBudget ? 'Budget' : 'Forecast'}: ${data.versionName}`
    : null

  // Scale sparkline by the max of budget OR revenue so revenue-only months still show
  const maxVal = Math.max(...data.months.flatMap(m => [m.budgetAmount, m.reportedRevenue]), 1)

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <TrendingUp size={14} style={{ color: 'var(--ns-brand-strong)', flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 13 }}>Revenue budget</span>
        {versionLabel && (
          <span className="muted" style={{ fontSize: 11.5 }}>{versionLabel}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>YTD vs target</span>
          <span style={{
            fontSize: 11.5, fontWeight: 600, padding: '2px 7px', borderRadius: 99, whiteSpace: 'nowrap',
            background: isAhead
              ? 'color-mix(in oklch, var(--ns-ok) 15%, transparent)'
              : 'color-mix(in oklch, var(--ns-warn) 15%, transparent)',
            color: isAhead ? 'var(--ns-ok)' : 'var(--ns-warn)',
          }}>
            {isAhead ? '+' : '−'}{fmtSEK(variance)}
          </span>
        </div>
      </div>

      {bm ? (
        <div>
          {/* Info grid — two balanced columns, no overflow */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', marginBottom: 8 }}>
            <span className="muted" style={{ fontSize: 11.5 }}>
              <strong style={{ color: 'var(--ns-text)', fontWeight: 500 }} className="tabular">
                {fmtSEK(bm.reportedRevenue)}
              </strong>{' '}reported
            </span>
            <span className="muted" style={{ fontSize: 11.5, textAlign: 'right' }}>
              {hasBudget ? (
                <>
                  <strong style={{ color: 'var(--ns-text)', fontWeight: 500 }} className="tabular">
                    {fmtSEK(bm.budgetAmount)}
                  </strong>{' '}budget
                </>
              ) : (
                <span style={{ color: 'var(--ns-warn)', fontSize: 11 }}>No budget this month</span>
              )}
            </span>
            <span className="muted" style={{ fontSize: 11 }}>{bm.month}</span>
            {hasBudget && (
              <span className="muted" style={{ fontSize: 11, textAlign: 'right' }}>
                {fmtSEK(bm.budgetPerWorkingDay)}/day · {bm.workingDays}d
              </span>
            )}
          </div>

          {hasBudget && (
            <>
              <div style={{ position: 'relative', height: 8, background: 'var(--ns-surface-2)', borderRadius: 99, overflow: 'visible' }}>
                <div style={{
                  position: 'absolute', top: -2, bottom: -2, left: `${daysPct}%`,
                  width: 2, background: 'var(--ns-text-muted)', borderRadius: 1, opacity: 0.4,
                  transform: 'translateX(-50%)', zIndex: 1,
                }} />
                <div style={{
                  height: '100%', borderRadius: 99,
                  width: `${monthPct}%`,
                  background: monthPct >= daysPct - 5 ? 'oklch(0.72 0.052 198)' : 'oklch(0.62 0.14 50)',
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span className="muted" style={{ fontSize: 10.5 }}>{monthPct.toFixed(0)}% of budget</span>
                <span className="muted" style={{ fontSize: 10.5, opacity: 0.7 }}>Pace: {daysPct.toFixed(0)}%</span>
              </div>
            </>
          )}
        </div>
      ) : (
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>No data for current month.</p>
      )}

      {/* Sparkline — budget ghost bars + revenue fill, scaled to max(budget, revenue) */}
      {data.months.length > 1 && (
        <div style={{ marginTop: 12, display: 'flex', gap: 3, alignItems: 'flex-end', height: 30 }}>
          {data.months.map(m => {
            const budgetH = Math.round((m.budgetAmount     / maxVal) * 24)
            const revH    = Math.round((m.reportedRevenue  / maxVal) * 24)
            const isCur   = m.month === curMonth
            return (
              <div
                key={m.month}
                title={`${m.month}: ${fmtSEK(m.reportedRevenue)}${m.budgetAmount ? ' / ' + fmtSEK(m.budgetAmount) + ' budget' : ''}`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
              >
                <div style={{ position: 'relative', width: '100%', height: 24 }}>
                  {budgetH > 0 && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      height: budgetH, background: 'var(--ns-surface-2)',
                      borderRadius: '2px 2px 0 0',
                    }} />
                  )}
                  {(revH > 0 || isCur) && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: '12%', right: '12%',
                      height: Math.max(revH, isCur ? 2 : 0),
                      background: isCur ? 'oklch(0.72 0.052 198)' : 'oklch(0.72 0.052 198 / 0.5)',
                      borderRadius: '2px 2px 0 0',
                      transition: 'height 0.3s',
                    }} />
                  )}
                </div>
                <span style={{ fontSize: 9, color: 'var(--ns-text-muted)', lineHeight: 1 }}>
                  {m.month.slice(5)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── KPI grid ──────────────────────────────────────────────────────────────────

function KpiGrid({ k }: { k: MyDashboardResponse['kpis'] }) {
  const { data: budget } = useQuery({
    queryKey: ['finance', 'my-budget'],
    queryFn:  () => api.get<MyBudgetResponse>('/finance/my-budget'),
    staleTime: 5 * 60 * 1000,
  })

  const today    = new Date()
  const curMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  const bm = budget?.configured ? budget.months?.find(m => m.month === curMonth) : undefined

  const todayTone =
    k.today.pct >= 100 ? 'good' :
    k.today.pct >= 50  ? '' : ''

  const weekTone =
    k.week.paceDelta < -2 ? 'warn' :
    k.week.paceDelta >  2 ? 'good' : ''

  const monthTone =
    k.month.pct >= (k.month.dayOfMonth / k.month.daysInMonth) * 100 - 5 ? 'good' : 'warn'

  const billableTone =
    k.billable.sharePct >= k.billable.targetPct ? 'good' : 'warn'

  return (
    <section className="mtd-kpi-grid">
      <div className="card mtd-kpi">
        <div className="mtd-kpi-label">Today</div>
        {k.today.target > 0 ? (
          <>
            <div className="mtd-kpi-value">
              {k.today.hours}<span className="unit"> / {k.today.target}h</span>
            </div>
            <div className={`mtd-bar ${todayTone}`}><div style={{ width: `${k.today.pct}%` }} /></div>
            <div className="mtd-kpi-meta">
              {k.today.pct}% of available · {k.today.remaining}h to go
            </div>
            {bm && bm.budgetPerWorkingDay > 0 && (
              <RevenueBar revenue={k.today.revenue} target={bm.budgetPerWorkingDay} />
            )}
          </>
        ) : (
          <>
            <div className="mtd-kpi-value">
              {k.today.hours}<span className="unit">h</span>
            </div>
            <div className="mtd-bar" style={{ visibility: 'hidden' }}><div /></div>
            <div className="mtd-kpi-meta">
              Day off — no hours expected{k.today.hours > 0 ? ` · ${k.today.hours}h logged` : ''}
            </div>
          </>
        )}
      </div>

      <div className="card mtd-kpi">
        <div className="mtd-kpi-label">This week</div>
        <div className="mtd-kpi-value">
          {k.week.hours}<span className="unit"> / {k.week.target}h</span>
        </div>
        <div className={`mtd-bar ${weekTone}`}><div style={{ width: `${k.week.pct}%` }} /></div>
        <div className={`mtd-kpi-meta ${weekTone}`}>
          {k.week.paceDelta >= 0
            ? `↑ ${k.week.paceDelta}h ahead of pace`
            : `↓ ${Math.abs(k.week.paceDelta)}h behind pace`}
        </div>
        {bm && bm.budgetPerWorkingDay > 0 && k.week.workingDay > 0 && (
          <RevenueBar revenue={k.week.revenue} target={bm.budgetPerWorkingDay * k.week.workingDay} />
        )}
      </div>

      <div className="card mtd-kpi">
        <div className="mtd-kpi-label">This month</div>
        <div className="mtd-kpi-value">
          {k.month.hours}<span className="unit"> / {k.month.target}h</span>
        </div>
        <div className={`mtd-bar ${monthTone}`}><div style={{ width: `${k.month.pct}%` }} /></div>
        <div className={`mtd-kpi-meta ${monthTone}`}>
          Day {k.month.dayOfMonth} of {k.month.daysInMonth} · {k.month.pct}% complete
        </div>
        {bm && bm.budgetAmount > 0 && (
          <RevenueBar revenue={k.month.revenue} target={bm.budgetAmount} />
        )}
      </div>

      <div className="card mtd-kpi">
        <div className="mtd-kpi-label">Billable share</div>
        <div className="mtd-kpi-value">
          {k.billable.sharePct}<span className="unit">%</span>
        </div>
        <div className={`mtd-bar ${billableTone}`}><div style={{ width: `${k.billable.sharePct}%` }} /></div>
        <div className={`mtd-kpi-meta ${billableTone}`}>
          {k.billable.last30Billable.toFixed(1)}h billable of {k.billable.last30Worked.toFixed(1)}h · target {k.billable.targetPct}%
        </div>
      </div>
    </section>
  )
}

function RevenueBar({ revenue, target }: { revenue: number; target: number }) {
  const pct = target > 0 ? Math.min(100, (revenue / target) * 100) : 0
  return (
    <div style={{ marginTop: 8, paddingTop: 7, borderTop: '1px solid var(--ns-border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>Revenue</span>
        <span style={{ fontSize: 11, color: 'var(--ns-text-muted)' }} className="tabular">
          {fmtSEKCompact(revenue)}
          <span style={{ opacity: 0.55 }}> / {fmtSEKCompact(target)}</span>
        </span>
      </div>
      <div style={{ height: 4, background: 'var(--ns-surface-2)', borderRadius: 99 }}>
        <div style={{
          height: '100%', borderRadius: 99, width: `${pct}%`,
          background: 'oklch(0.72 0.052 198)',
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  )
}

// ── Production rate (8 weeks × 5 days, stacked by customer) ─────────────────

function ProductionRateCard({ rows, weekStart }: { rows: ProductionRateRow[]; weekStart: string }) {
  // Roll up rows into weeks → days → customer-stacks.
  const { weeks, totalHours, totalDays, missingDays } = useMemo(() => {
    type DayBucket = { date: string; bars: { customerId: number | null; customerName: string; hours: number }[]; total: number; missing: boolean }
    const start = new Date(weekStart + 'T00:00:00')
    // 8 weeks total, current week is the last one.
    const startDate = new Date(start); startDate.setDate(start.getDate() - 7 * 7)

    const weeksOut: { weekNumber: number; days: DayBucket[] }[] = []
    for (let w = 0; w < 8; w++) {
      const wkStart = new Date(startDate); wkStart.setDate(startDate.getDate() + 7 * w)
      const wkNum   = isoWeek(wkStart)
      const days: DayBucket[] = []
      for (let d = 0; d < 5; d++) {
        const day = new Date(wkStart); day.setDate(wkStart.getDate() + d)
        const iso = day.toISOString().slice(0, 10)
        days.push({ date: iso, bars: [], total: 0, missing: false })
      }
      weeksOut.push({ weekNumber: wkNum, days })
    }

    let totalH = 0, totalD = 0, missing = 0
    rows.forEach(r => {
      // Find the week+day bucket.
      const d = new Date(r.entryDate + 'T00:00:00')
      const diffDays = Math.floor((d.getTime() - startDate.getTime()) / 86400000)
      const w = Math.floor(diffDays / 7)
      const dow = (d.getDay() + 6) % 7  // Mon=0
      if (w < 0 || w > 7 || dow > 4) return
      const bucket = weeksOut[w].days[dow]
      bucket.bars.push({ customerId: r.customerId, customerName: r.customerName, hours: r.hours })
      bucket.total += r.hours
      totalH += r.hours
    })
    weeksOut.forEach(wk => wk.days.forEach(day => {
      const dayDate = new Date(day.date + 'T00:00:00')
      const elapsed = dayDate.getTime() < new Date().setHours(0,0,0,0)
      if (day.total > 0) totalD++
      if (elapsed && day.total === 0) { day.missing = true; missing++ }
    }))
    return { weeks: weeksOut, totalHours: totalH, totalDays: totalD, missingDays: missing }
  }, [rows, weekStart])

  const avgPerDay  = totalDays > 0 ? totalHours / totalDays : 0
  const avgPerWeek = totalHours / 8
  const capacity   = Math.round((avgPerDay / 8) * 100)

  // Distinct customers for the legend.
  const legend = useMemo(() => {
    const seen = new Map<number | null, string>()
    rows.forEach(r => { if (!seen.has(r.customerId)) seen.set(r.customerId, r.customerName) })
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name, color: customerColor(id) }))
  }, [rows])

  // SVG geometry: 800×220 viewBox, day bar = 14px wide, 2px gap, week = 5 bars + gap.
  const SVG_W = 800
  const SVG_H = 220
  const Y0 = 20    // top
  const Y_ZERO = 155 // y at 0h
  const Y_TARGET = 65 // 8h
  const PX_PER_HR = (Y_ZERO - 20) / 12  // 12h max axis

  const BAR_W = 14
  const BAR_GAP = 2
  const WEEK_W = 5 * BAR_W + 4 * BAR_GAP // 78
  const WEEK_GAP = 17
  const ORIGIN_X = 38

  return (
    <div className="card">
      <div className="mtd-card-head">
        <div>
          <div className="mtd-card-title">Production rate · last 8 weeks</div>
          <div className="mtd-card-sub">Daily hours, stacked by customer. Dashed line = 8h target.</div>
        </div>
      </div>

      <div className="mtd-prod-stats">
        <Stat num={`${avgPerDay.toFixed(1)}h`} lbl="Avg per working day" />
        <Stat num={`${avgPerWeek.toFixed(1)}h`} lbl="Avg per week" />
        <Stat num={`${capacity}%`}              lbl="Of capacity" />
        <Stat num={`${missingDays}`}            lbl="Days unreported" tone={missingDays > 0 ? 'warn' : ''} />
      </div>

      <svg className="mtd-chart" viewBox={`0 0 ${SVG_W} ${SVG_H}`} preserveAspectRatio="none">
        {/* gridlines */}
        <g fontFamily="Inter" fontSize={10} fill="var(--ns-text-subtle)" fontWeight={500}>
          <line x1={ORIGIN_X} y1={Y0}        x2={SVG_W} y2={Y0}        stroke="var(--ns-border)" strokeDasharray="2,3"/>
          <text x={4}  y={Y0 + 4}>12h</text>
          <line x1={ORIGIN_X} y1={Y_TARGET}  x2={SVG_W} y2={Y_TARGET}  stroke="var(--ns-text-subtle)" strokeDasharray="3,3"/>
          <text x={4}  y={Y_TARGET + 4} fill="var(--ns-text)" fontWeight={600}>8h</text>
          <line x1={ORIGIN_X} y1={110}       x2={SVG_W} y2={110}       stroke="var(--ns-border)"/>
          <text x={4}  y={114}>4h</text>
          <line x1={ORIGIN_X} y1={Y_ZERO}    x2={SVG_W} y2={Y_ZERO}    stroke="var(--ns-border)"/>
          <text x={4}  y={Y_ZERO + 4}>0</text>
        </g>

        {/* Bars */}
        {weeks.map((wk, wi) => {
          const wx = ORIGIN_X + wi * (WEEK_W + WEEK_GAP) + 8
          return (
            <g key={wi} transform={`translate(${wx},0)`}>
              {wk.days.map((day, di) => {
                const x = di * (BAR_W + BAR_GAP)
                if (day.missing) {
                  return (
                    <rect key={di} x={x} y={Y_ZERO - 5} width={BAR_W} height={5}
                          fill="var(--ns-warn-soft, oklch(0.95 0.04 80))"
                          stroke="var(--ns-warn-strong, oklch(0.65 0.18 60))"
                          strokeDasharray="2,2" strokeWidth={1}/>
                  )
                }
                let yCursor = Y_ZERO
                return (
                  <g key={di}>
                    {day.bars.map((b, bi) => {
                      const h = b.hours * PX_PER_HR
                      yCursor -= h
                      return (
                        <rect key={bi} x={x} y={yCursor} width={BAR_W} height={h}
                              fill={customerColor(b.customerId)}>
                          <title>{b.customerName}: {b.hours.toFixed(1)}h on {day.date}</title>
                        </rect>
                      )
                    })}
                  </g>
                )
              })}
            </g>
          )
        })}

        {/* Week labels */}
        <g fontFamily="Inter" fontSize={10} fill="var(--ns-text-subtle)" textAnchor="middle">
          {weeks.map((wk, wi) => {
            const cx = ORIGIN_X + wi * (WEEK_W + WEEK_GAP) + 8 + WEEK_W / 2
            const isCurrent = wi === 7
            return (
              <text key={wi} x={cx} y={180}
                    fill={isCurrent ? 'var(--ns-brand-strong)' : undefined}
                    fontWeight={isCurrent ? 600 : 500}>
                W{wk.weekNumber}
              </text>
            )
          })}
        </g>
      </svg>

      <div className="mtd-legend">
        {legend.map(l => (
          <div key={String(l.id)} className="mtd-legend-item">
            <div className="mtd-legend-swatch" style={{ background: l.color }} />
            {l.name}
          </div>
        ))}
        {missingDays > 0 && (
          <div className="mtd-legend-item">
            <div className="mtd-legend-swatch" style={{
              background: 'var(--ns-warn-soft, oklch(0.95 0.04 80))',
              border:     '1px dashed var(--ns-warn-strong, oklch(0.65 0.18 60))',
            }}/>
            Missing report
          </div>
        )}
      </div>
    </div>
  )
}

function Stat({ num, lbl, tone = '' }: { num: string; lbl: string; tone?: string }) {
  return (
    <div className="mtd-stat">
      <div className={`mtd-stat-num ${tone}`}>{num}</div>
      <div className="mtd-stat-lbl">{lbl}</div>
    </div>
  )
}

function isoWeek(d: Date): number {
  const target = new Date(d); target.setHours(0, 0, 0, 0)
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7))
  const week1 = new Date(target.getFullYear(), 0, 4)
  return 1 + Math.round(((target.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
}

// ── Where time goes (donut + list) ──────────────────────────────────────────

function WhereTimeGoesCard({ total, rows }: { total: number; rows: { customerId: number | null; customerName: string; hours: number; sharePct: number }[] }) {
  // Build SVG dasharrays. Donut radius = 15.915 → circumference 100.
  const segments = useMemo(() => {
    let offset = 0
    return rows.map(r => {
      const pct = total > 0 ? (r.hours / total) * 100 : 0
      const seg = { ...r, pct, dasharray: `${pct} ${100 - pct}`, offset: -offset }
      offset += pct
      return seg
    })
  }, [rows, total])

  return (
    <div className="card">
      <div className="mtd-card-head">
        <div>
          <div className="mtd-card-title">Where time goes</div>
          <div className="mtd-card-sub">Last 30 days, by customer</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="muted" style={{ textAlign: 'center', padding: 24, fontSize: 13 }}>
          No hours logged in the last 30 days.
        </div>
      ) : (
        <div className="mtd-donut-wrap">
          <div className="mtd-donut">
            <svg viewBox="0 0 42 42" width="180" height="180" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="21" cy="21" r="15.915" fill="none" stroke="var(--ns-surface-2)" strokeWidth="6"/>
              {segments.map(s => (
                <circle key={String(s.customerId)} cx="21" cy="21" r="15.915" fill="none"
                        stroke={customerColor(s.customerId)} strokeWidth="6"
                        strokeDasharray={s.dasharray} strokeDashoffset={s.offset}>
                  <title>{s.customerName}: {s.hours.toFixed(1)}h ({s.sharePct.toFixed(0)}%)</title>
                </circle>
              ))}
            </svg>
            <div className="mtd-donut-center">
              <div className="mtd-donut-total">{total.toFixed(0)}h</div>
              <div className="mtd-donut-lbl">Last 30 days</div>
            </div>
          </div>

          <div className="mtd-donut-list">
            {rows.map(r => (
              <div key={String(r.customerId)} className="mtd-donut-row">
                <div className="mtd-donut-swatch" style={{ background: customerColor(r.customerId) }} />
                <div className="mtd-donut-name">{r.customerName}</div>
                <div className="mtd-donut-amt">
                  {r.hours.toFixed(1)}h<span className="mtd-donut-pct"> {r.sharePct.toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Compliance heatmap (4 weeks × 5 working days) ───────────────────────────

function ComplianceCard({ days, weekStart }: { days: { date: string; hours: number }[]; weekStart: string }) {
  const grid = useMemo(() => {
    const map = new Map(days.map(d => [d.date, d.hours]))
    const today = new Date(); today.setHours(0,0,0,0)
    const start = new Date(weekStart + 'T00:00:00')
    const startDate = new Date(start); startDate.setDate(start.getDate() - 7 * 3)
    const out: { weekNumber: number; isCurrent: boolean; days: { date: string; hours: number; missing: boolean; isFuture: boolean; isToday: boolean }[] }[] = []
    for (let w = 0; w < 4; w++) {
      const wkStart = new Date(startDate); wkStart.setDate(startDate.getDate() + 7 * w)
      const wkNum = isoWeek(wkStart)
      const ds = []
      for (let d = 0; d < 5; d++) {
        const day = new Date(wkStart); day.setDate(wkStart.getDate() + d)
        const iso = day.toISOString().slice(0, 10)
        const hrs = map.get(iso) ?? 0
        const isFuture = day.getTime() > today.getTime()
        const isToday  = day.getTime() === today.getTime()
        const missing  = !isFuture && !isToday && hrs < 6
        ds.push({ date: iso, hours: hrs, missing, isFuture, isToday })
      }
      out.push({ weekNumber: wkNum, isCurrent: w === 3, days: ds })
    }
    return out
  }, [days, weekStart])

  const total = days.reduce((s, d) => s + d.hours, 0)
  const reportedDays = days.filter(d => d.hours > 0).length
  const avg = reportedDays > 0 ? total / reportedDays : 0

  function cellClass(hrs: number, missing: boolean, isFuture: boolean, isToday: boolean): string {
    if (isFuture) return 'mtd-heat-cell mtd-heat-future'
    if (missing)  return 'mtd-heat-cell mtd-heat-miss'
    if (hrs <= 0) return 'mtd-heat-cell mtd-heat-h0'
    if (hrs < 4)  return `mtd-heat-cell mtd-heat-h1${isToday ? ' mtd-heat-today' : ''}`
    if (hrs < 6)  return `mtd-heat-cell mtd-heat-h2${isToday ? ' mtd-heat-today' : ''}`
    if (hrs < 8)  return `mtd-heat-cell mtd-heat-h3${isToday ? ' mtd-heat-today' : ''}`
    return         `mtd-heat-cell mtd-heat-h4${isToday ? ' mtd-heat-today' : ''}`
  }

  return (
    <div className="card">
      <div className="mtd-card-head">
        <div>
          <div className="mtd-card-title">Reporting compliance · last 4 weeks</div>
          <div className="mtd-card-sub">Each cell is one working day. Yellow = below 6h on a past day.</div>
        </div>
      </div>

      <div className="mtd-heat-rows">
        <div className="mtd-heat-row mtd-heat-head">
          <div />
          {grid.map(wk => (
            <div key={wk.weekNumber}
                 className={wk.isCurrent ? 'mtd-heat-week-current' : ''}
                 style={{ gridColumn: 'span 5', textAlign: 'center' }}>
              Week {wk.weekNumber}
            </div>
          ))}
        </div>
        <div className="mtd-heat-row">
          <div className="mtd-heat-axis">Hours</div>
          {grid.flatMap(wk =>
            wk.days.map(d => (
              <div key={d.date}
                   className={cellClass(d.hours, d.missing, d.isFuture, d.isToday)}
                   title={`${d.date} — ${d.hours.toFixed(1)}h${d.missing ? ' (missing)' : d.isToday ? ' (today)' : ''}`} />
            ))
          )}
        </div>
      </div>

      <div className="mtd-heat-foot">
        <div>
          Avg working day: <strong>{avg.toFixed(1)}h</strong>
        </div>
        <div className="mtd-heat-key">
          Less
          <div className="mtd-heat-cell mtd-heat-h0" />
          <div className="mtd-heat-cell mtd-heat-h1" />
          <div className="mtd-heat-cell mtd-heat-h2" />
          <div className="mtd-heat-cell mtd-heat-h3" />
          <div className="mtd-heat-cell mtd-heat-h4" />
          More
        </div>
      </div>
    </div>
  )
}

// ── By activity ──────────────────────────────────────────────────────────────

function ByActivityCard({ rows }: { rows: { activityId: number | null; activityName: string; hours: number; sharePct: number }[] }) {
  const max = rows.length > 0 ? Math.max(...rows.map(r => r.hours), 1) : 1

  return (
    <div className="card">
      <div className="mtd-card-head">
        <div>
          <div className="mtd-card-title">By activity</div>
          <div className="mtd-card-sub">Last 30 days</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="muted" style={{ textAlign: 'center', padding: 24, fontSize: 13 }}>
          No activities logged.
        </div>
      ) : (
        <div className="mtd-activity-list">
          {rows.slice(0, 8).map(r => (
            <div key={String(r.activityId)} className="mtd-activity-item">
              <div className="mtd-activity-row">
                <span className="mtd-activity-name">{r.activityName}</span>
                <span className="mtd-activity-amt">{r.hours.toFixed(1)}h · {r.sharePct.toFixed(0)}%</span>
              </div>
              <div className="mtd-activity-bar">
                <div style={{
                  width: `${(r.hours / max) * 100}%`,
                  background: customerColor(r.activityId),
                }}/>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Planned work ─────────────────────────────────────────────────────────────

const STATUS_PILL: Record<PlannedWorkStatus, { className: string; label: (deltaHours: number) => string }> = {
  over:    { className: 'pill-danger',  label: d => `Over +${Math.abs(d).toFixed(1)}h` },
  behind:  { className: 'pill-warn',    label: d => `Behind ${Math.abs(d).toFixed(1)}h` },
  ahead:   { className: 'pill-good',    label: _ => 'Ahead' },
  ontrack: { className: 'pill-good',    label: _ => 'On track' },
  ongoing: { className: 'pill-neutral', label: _ => 'Ongoing' },
}

function PlannedWorkCard({ rows }: { rows: MyDashboardResponse['plannedWork'] }) {
  return (
    <section className="card mtd-section">
      <div className="mtd-card-head">
        <div>
          <div className="mtd-card-title">Planned work · {rows.length} active assignment{rows.length === 1 ? '' : 's'}</div>
          <div className="mtd-card-sub">Phase budget vs. logged hours. Black tick = today's expected progress.</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="muted" style={{ textAlign: 'center', padding: 24, fontSize: 13 }}>
          No active assignments. Ask your manager to add you to a phase, or log time freely under the default phase.
        </div>
      ) : (
        <div className="mtd-planned-table">
          <div className="mtd-planned-row mtd-planned-head">
            <div>Customer / Project</div>
            <div>Phase</div>
            <div>Progress</div>
            <div>Schedule</div>
            <div style={{ textAlign: 'right' }}>Status</div>
          </div>

          {rows.map(r => {
            const pct  = r.budgetHours > 0 ? Math.min((r.loggedHours / r.budgetHours) * 100, 100) : 0
            const tone = r.status === 'over' ? 'over' : r.status === 'behind' ? 'warn' : r.status === 'ahead' ? 'good' : ''
            const pill = STATUS_PILL[r.status]
            return (
              <div key={r.phaseResourceId} className="mtd-planned-row">
                <div>
                  <div style={{ fontWeight: 500 }}>{r.customerName ?? r.projectName}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{r.projectName}</div>
                </div>
                <div>{r.phaseName}</div>
                <div>
                  <div className={`mtd-pbar ${tone}`}>
                    <div className="mtd-pbar-fill" style={{ width: `${pct}%` }} />
                    {r.expectedPct != null && r.budgetHours > 0 && (
                      <div className="mtd-pbar-mark" style={{ left: `${(r.expectedPct * 100).toFixed(1)}%` }} />
                    )}
                  </div>
                  <div className="mtd-pbar-meta">
                    <span>{r.loggedHours}h logged</span>
                    <span>of {r.budgetHours > 0 ? `${r.budgetHours}h` : '—'}</span>
                  </div>
                </div>
                <div className="mono" style={{ fontSize: 12 }}>
                  {r.startDate ?? '—'} → {r.endDate ?? '—'}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className={`badge ${pill.className}`}>{pill.label(r.deltaHours)}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ── ADO tasks ────────────────────────────────────────────────────────────────

function AdoCard({ rows }: { rows: MyDashboardResponse['adoTasks'] }) {
  return (
    <section className="card mtd-section">
      <div className="mtd-card-head">
        <div>
          <div className="mtd-card-title">Azure DevOps · {rows.length} active</div>
          <div className="mtd-card-sub">Items assigned to you</div>
        </div>
        <Link to="/settings/integrations/azure-devops" className="mtd-card-link">
          Open in Forge <ExternalLink size={11} />
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="muted" style={{ textAlign: 'center', padding: 24, fontSize: 13 }}>
          No active work items assigned to you.
        </div>
      ) : (
        <div className="mtd-task-list">
          {rows.map(t => (
            <div key={t.id} className="mtd-task">
              <div className="mtd-task-source">A</div>
              <div className="mtd-task-mid">
                <div className="mtd-task-title">{t.title}</div>
                <div className="mtd-task-meta">
                  <span className="mono">#{t.id}</span>
                  <span>{t.workItemType}</span>
                  {t.projectName && <span>{t.projectName}</span>}
                  {t.iterationPath && <span>{t.iterationPath.split('\\').pop()}</span>}
                </div>
              </div>
              <span className={`badge ${t.state.toLowerCase().includes('progress') ? 'pill-warn' : 'pill-neutral'}`}>{t.state}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
